import { ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, shareReplay, take } from 'rxjs';
import { ImageCroppedEvent, ImageTransform, LoadedImage } from 'ngx-image-cropper';
import { AiImageDialogData, AiImageDialogResult, CachedGeneration, GenerateImageResponse, OrgGalleryImage } from '../../../models/ai-image.model';
import { AiImageService } from '../../../services/ai-image.service';
import { UploadService } from '../../../services/upload.service';
import { OrgImageGalleryService } from '../../../services/org-image-gallery.service';

type DialogState = 'idle' | 'generating' | 'results' | 'selected' | 'cropping' | 'gallery' | 'gallery-selected';

@Component({
    standalone: false,
    selector: 'por-ai-image-generator-dialog',
    templateUrl: './ai-image-generator-dialog.component.html',
    styleUrls: ['./ai-image-generator-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [OrgImageGalleryService]
})
export class AiImageGeneratorDialogComponent implements OnInit, OnDestroy {
    private static readonly cacheKey = 'ai-image-generator-cache-v2';
    private static readonly cacheTtlMs = 60 * 60 * 1000;
    private static readonly maxScale = 3;
    private static readonly imageCount = 4;

    readonly state = signal<DialogState>('idle');
    // True from the moment Generate is clicked until every request has settled — drives the
    // button disabled/spinner state independently of `state` (which flips to 'results' early so
    // the grid can show placeholders while requests are still in flight).
    readonly generating = signal<boolean>(false);
    // Count of requests still in flight (started, not yet settled). Each renders a busy placeholder
    // slot in the grid; a slot disappears when its request fails, fills in when it succeeds.
    readonly pendingImages = signal<number>(0);
    readonly selectedTabIndex = signal<number>(0);
    readonly cropperReady = signal<boolean>(false);
    readonly uploading = signal<boolean>(false);
    readonly preparingCrop = signal<boolean>(false);
    readonly cropSource = signal<'generate' | 'gallery'>('generate');
    readonly selectedGalleryImage = signal<OrgGalleryImage | null>(null);
    readonly selectedImageFile = signal<File | null>(null);
    readonly showReplaceConfirmation = signal<boolean>(false);
    readonly hourlyUsed = signal<number | null>(null);
    readonly hourlyLimit = signal<number | null>(null);
    readonly hourlyRemaining = signal<number | null>(null);
    readonly hourlyLimitReached = signal<boolean>(false);
    readonly transform = signal<ImageTransform>({});
    readonly minScale = signal<number>(1);
    readonly defaultScale = signal<number>(1);
    readonly backgroundColor = signal<'#ffffff' | '#000000' | 'transparent'>('#ffffff');
    private readonly blobCache = new Map<string, Observable<Blob>>();
    private zoomRafHandle: number | null = null;
    @ViewChild('dialogContent') dialogContent?: ElementRef;

    prompt = '';
    generatedImages: GenerateImageResponse[] = [];
    selectedImage: GenerateImageResponse | null = null;
    errorMessage = '';
    fromCache = false;
    croppedBlob: Blob | null = null;
    croppedPreviewUrl: string | null = null;

    readonly data = inject<AiImageDialogData>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject<MatDialogRef<AiImageGeneratorDialogComponent>>(MatDialogRef);
    private readonly aiImageService = inject(AiImageService);
    private readonly uploadService = inject(UploadService);
    readonly galleryService = inject(OrgImageGalleryService);

    ngOnInit(): void {
        const cached = this.loadFromCache();
        if (cached) {
            this.prompt = cached.prompt;
            this.generatedImages = cached.images;
            this.state.set('results');
            this.fromCache = true;
        }
    }

    generate(): void {
        this.errorMessage = '';
        this.fromCache = false;
        this.hourlyLimitReached.set(false);
        this.generatedImages = [];
        this.generating.set(true);
        // Show the grid immediately with a busy placeholder per in-flight request, so a single
        // image that lands first doesn't make it look like generation is already done.
        this.pendingImages.set(AiImageGeneratorDialogComponent.imageCount);
        this.state.set('results');
        let limitReached = false;

        this.aiImageService
            .generateImagesStream(
                {
                    prompt: this.prompt,
                    generationType: this.data.generationType,
                    targetWidth: this.data.targetWidth,
                    targetHeight: this.data.targetHeight
                },
                AiImageGeneratorDialogComponent.imageCount
            )
            .subscribe({
                next: (response: GenerateImageResponse | null) => {
                    // One emission == one settled request (success response, rate-limit block, or
                    // a `null` failure marker). Retire its busy slot either way; a request still
                    // retrying hasn't emitted yet, so its placeholder keeps spinning.
                    this.pendingImages.update(n => Math.max(0, n - 1));
                    if (!response) {
                        return;
                    }
                    this.applyRateLimit(response);
                    if (response.rateLimit && !response.rateLimit.imageRequestAllowed) {
                        limitReached = true;
                        return;
                    }
                    if (response.image) {
                        // Append as each image arrives; a request recovered by a retry appears later.
                        this.generatedImages = [...this.generatedImages, response];
                        this.saveToCache(this.generatedImages);
                    }
                },
                complete: () => {
                    this.generating.set(false);
                    this.pendingImages.set(0);
                    if (this.generatedImages.length === 0) {
                        if (limitReached) {
                            this.hourlyLimitReached.set(true);
                        } else {
                            this.errorMessage = 'aiImageGenerateError';
                        }
                        this.state.set('idle');
                    }
                },
                error: () => {
                    this.generating.set(false);
                    this.pendingImages.set(0);
                    if (this.generatedImages.length === 0) {
                        this.state.set('idle');
                        this.errorMessage = 'aiImageGenerateError';
                    }
                }
            });
    }

    /** Index array (one entry per in-flight request) used to render busy placeholder slots. */
    pendingSlots(): number[] {
        return Array.from({ length: this.pendingImages() }, (_, i) => i);
    }

    private applyRateLimit(response: GenerateImageResponse): void {
        const info = response.rateLimit;
        if (!info) {
            return;
        }
        this.hourlyUsed.set(info.used);
        this.hourlyLimit.set(info.limit);
        this.hourlyRemaining.set(info.remaining);
    }

    selectImage(image: GenerateImageResponse): void {
        this.selectedImage = image;
        this.state.set('selected');
    }

    useSelectedImage(): void {
        if (!this.selectedImage?.image) {
            return;
        }
        this.cropSource.set('generate');
        this.enterCroppingWithFetch(this.selectedImage.image.url);
    }

    onImageCropped(event: ImageCroppedEvent): void {
        this.croppedBlob = event.blob ?? null;
        this.croppedPreviewUrl = event.objectUrl ?? null;
    }

    onImageLoaded(loaded: LoadedImage): void {
        const imgW = loaded.original.size.width;
        const imgH = loaded.original.size.height;
        if (!imgW || !imgH) {
            return;
        }
        const imageAspect = imgH / imgW;
        const cropAspect = this.cropTargetHeight / this.cropTargetWidth;
        const fitAllScale = imageAspect > cropAspect ? cropAspect / imageAspect : imageAspect / cropAspect;
        const isTallerThanCrop = imageAspect > cropAspect;
        const initialScale = isTallerThanCrop ? fitAllScale : 1;

        // Store the scales; the transform is applied in onCropperReady so the crop never runs
        // before the cropper box is established (an early 0-width crop throws IndexSizeError).
        this.minScale.set(fitAllScale);
        this.defaultScale.set(initialScale);
    }

    onCropperReady(): void {
        this.transform.set({ scale: this.defaultScale(), translateH: 0, translateV: 0 });
        this.cropperReady.set(true);
    }

    onZoomChange(scale: number): void {
        if (this.zoomRafHandle !== null) {
            cancelAnimationFrame(this.zoomRafHandle);
        }
        this.zoomRafHandle = requestAnimationFrame(() => {
            this.zoomRafHandle = null;
            this.transform.update(t => ({ ...t, scale }));
        });
    }

    onBackgroundChange(color: '#ffffff' | '#000000' | 'transparent'): void {
        this.backgroundColor.set(color);
    }

    resetZoom(): void {
        if (this.zoomRafHandle !== null) {
            cancelAnimationFrame(this.zoomRafHandle);
            this.zoomRafHandle = null;
        }
        this.transform.set({ scale: this.defaultScale(), translateH: 0, translateV: 0 });
    }

    onTransformChange(next: ImageTransform): void {
        this.transform.set(next);
    }

    ngOnDestroy(): void {
        if (this.zoomRafHandle !== null) {
            cancelAnimationFrame(this.zoomRafHandle);
            this.zoomRafHandle = null;
        }
    }

    onCropperFailed(): void {
        this.errorMessage = 'aiImageApiError';
        this.state.set('results');
    }

    applyCrop(): void {
        if (!this.croppedBlob) {
            return;
        }
        const file = this.buildCroppedFile(this.croppedBlob);

        if (this.data.currentImageUrl) {
            this.showReplaceConfirmation.set(true);
            this.scrollToConfirmation();
        } else {
            this.uploadCroppedImage(file);
        }
    }

    private scrollToConfirmation(): void {
        setTimeout(() => {
            const el = this.dialogContent?.nativeElement;
            if (el) {
                el.scrollTop = el.scrollHeight;
            }
        });
    }

    confirmReplace(): void {
        if (!this.croppedBlob) {
            return;
        }
        const file = this.buildCroppedFile(this.croppedBlob);
        this.uploadCroppedImage(file);
    }

    cancelReplace(): void {
        this.showReplaceConfirmation.set(false);
        this.state.set('cropping');
    }

    backToSource(): void {
        this.selectedImageFile.set(null);
        if (this.cropSource() === 'gallery') {
            this.state.set('results');
            this.selectedTabIndex.set(1);
            this.selectedImage = null;
            this.croppedBlob = null;
            this.cropperReady.set(false);
        } else {
            this.state.set('results');
            this.selectedImage = null;
            this.croppedBlob = null;
            this.cropperReady.set(false);
        }
    }

    cancel(): void {
        this.dialogRef.close(undefined);
    }

    onTabChange(index: number): void {
        this.selectedTabIndex.set(index);
        if (index === 1 && !this.galleryService['currentImageType']) {
            this.galleryService.loadGallery(this.data.generationType);
        }
    }

    selectGalleryImage(img: OrgGalleryImage): void {
        this.selectedGalleryImage.set(img);
    }

    useGalleryImage(): void {
        const img = this.selectedGalleryImage();
        if (!img) return;
        this.selectedImage = {
            image: { url: img.imageUrl, index: 0 },
            estimatedCostUsd: undefined,
            rateLimit: { used: 0, limit: 0, remaining: 0, imageRequestAllowed: true }
        };
        this.cropSource.set('gallery');
        this.enterCroppingWithFetch(img.imageUrl);
    }

    private enterCroppingWithFetch(s3Url: string): void {
        this.state.set('cropping');
        this.cropperReady.set(false);
        this.croppedBlob = null;
        this.selectedImageFile.set(null);
        this.preparingCrop.set(true);
        this.errorMessage = '';
        this.transform.set({});
        this.minScale.set(1);
        this.defaultScale.set(1);

        let cached = this.blobCache.get(s3Url);
        if (!cached) {
            cached = this.aiImageService.proxyImage(s3Url).pipe(shareReplay({ bufferSize: 1, refCount: false }));
            this.blobCache.set(s3Url, cached);
        }

        cached.pipe(take(1)).subscribe({
            next: blob => {
                this.selectedImageFile.set(this.toCropperFile(blob, s3Url));
                this.preparingCrop.set(false);
            },
            error: () => {
                this.preparingCrop.set(false);
                this.errorMessage = 'aiImageApiError';
                this.state.set('results');
            }
        });
    }

    private toCropperFile(blob: Blob, s3Url: string): File {
        const segments = s3Url.split('/');
        const fallbackName = segments[segments.length - 1] || 'image';
        const type = blob.type || 'image/jpeg';
        return new File([blob], fallbackName, { type });
    }

    get cropAspectRatio(): number {
        const w = this.data.targetWidth ?? (this.data.generationType === 'banner' ? 324 : 300);
        const h = this.data.targetHeight ?? (this.data.generationType === 'banner' ? 144 : 74);
        return w / h;
    }

    get cropTargetWidth(): number {
        return this.data.targetWidth ?? (this.data.generationType === 'banner' ? 324 : 300);
    }

    get cropTargetHeight(): number {
        return this.data.targetHeight ?? (this.data.generationType === 'banner' ? 144 : 74);
    }

    get cropFormat(): 'png' | 'jpeg' | 'webp' | 'bmp' | 'ico' {
        return this.data.generationType === 'banner' ? 'jpeg' : 'png';
    }

    get showTransparentBg(): boolean {
        return this.data.generationType === 'logo';
    }

    get maxScale(): number {
        return AiImageGeneratorDialogComponent.maxScale;
    }

    private buildCroppedFile(blob: Blob): File {
        const ext = this.data.generationType === 'banner' ? 'jpg' : 'png';
        const mimeType = this.data.generationType === 'banner' ? 'image/jpeg' : 'image/png';
        return new File([blob], `ai-cropped.${ext}`, { type: mimeType });
    }

    private uploadCroppedImage(file: File): void {
        this.uploading.set(true);
        this.showReplaceConfirmation.set(false);
        this.uploadService
            .uploadFile(file, this.data.generationType)
            .pipe(take(1))
            .subscribe({
                next: response => {
                    this.uploading.set(false);
                    if (response.url) {
                        this.dialogRef.close({ url: response.url } as AiImageDialogResult);
                    } else {
                        this.errorMessage = 'aiImageApiError';
                        this.state.set('cropping');
                    }
                },
                error: () => {
                    this.uploading.set(false);
                    this.errorMessage = 'aiImageApiError';
                    this.state.set('cropping');
                }
            });
    }

    private saveToCache(images: GenerateImageResponse[]): void {
        const cache: CachedGeneration = {
            prompt: this.prompt,
            generationType: this.data.generationType,
            images,
            timestamp: Date.now()
        };
        const allCaches = this.getAllCaches();
        const index = allCaches.findIndex(c => c.generationType === this.data.generationType);
        if (index >= 0) {
            allCaches[index] = cache;
        } else {
            allCaches.push(cache);
        }
        localStorage.setItem(AiImageGeneratorDialogComponent.cacheKey, JSON.stringify(allCaches));
    }

    private loadFromCache(): CachedGeneration | null {
        const allCaches = this.getAllCaches();
        const cached = allCaches.find(c => c.generationType === this.data.generationType);
        if (cached && Date.now() - cached.timestamp < AiImageGeneratorDialogComponent.cacheTtlMs) {
            return cached;
        }
        return null;
    }

    private getAllCaches(): CachedGeneration[] {
        const raw = localStorage.getItem(AiImageGeneratorDialogComponent.cacheKey);
        return raw ? (JSON.parse(raw) as CachedGeneration[]) : [];
    }
}
